import React, { useEffect } from "react";

import type { ICellEditorParams } from "ag-grid-community";

/**
 * Result returned to AG Grid from `cellEditorSelector`. Mirrors AG Grid's
 * `CellEditorSelectorResult` minus the optional `popupPosition` we don't
 * need today.
 */
export interface CellEditorOverrideResult {
  component: React.ComponentType<unknown> | string;
  popup?: boolean;
  params?: Record<string, unknown>;
}

/**
 * Cell editor override hook.
 *
 * Receives the AG Grid `ICellEditorParams` for a cell that is about to
 * enter edit mode, and returns either a `{ component, popup, params }`
 * descriptor to use that editor, or `undefined` to fall through to the
 * cellSpec-driven default in `cellEditorSelector`.
 *
 * This is the right extension point when the consumer wants to register
 * a typed editor (e.g. a typed-amount popover that edits a `{type,
 * amount}` pair) for specific benefit keys, without mutating the
 * underlying cellSpec.
 */
export type CellEditorOverride = (
  params: ICellEditorParams,
) => CellEditorOverrideResult | undefined;

// AG Grid calls `cellEditorSelector` as a plain function (not from a
// React component), so the override is held in a module-level ref that
// the provider wires up via `useEffect` on mount/unmount. Only one
// override may be active at a time.
let activeOverride: CellEditorOverride | null = null;

/** Internal — read by `cellEditorSelector` in `buildColumns.ts`. */
export const getActiveCellEditorOverride = (): CellEditorOverride | null =>
  activeOverride;

interface CellEditorOverrideProviderProps {
  override: CellEditorOverride;
  children: React.ReactNode;
}

export const CellEditorOverrideProvider = ({
  override,
  children,
}: CellEditorOverrideProviderProps): React.JSX.Element => {
  // Set synchronously during render so `cellEditorSelector` (called by AG
  // Grid on the same tick as a user click) always sees the current override.
  // A useEffect would fire after paint, leaving a window where the module-level
  // ref is stale and cell clicks fall through to the wrong editor.
  // eslint-disable-next-line react-compiler/react-compiler
  activeOverride = override;

  // Cleanup on unmount only — runs after the component is removed from the tree.
  useEffect(
    () => () => {
      if (activeOverride === override) {
        activeOverride = null;
      }
    },
    [override],
  );

  return <>{children}</>;
};
